import { Channel, Team } from 'types';
import TopChannel from '../Text/Top/TopChannel';
import Icon from '@/icons';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QueryObserverResult, useQuery, useQueryClient } from '@tanstack/react-query';
import ErrorBox from '@/components/Error/Box';
import TodoChannelItem from '@/components/Channel/Todo/Item';
import getPermissionsById from '@/components/Dialogs/ChannelSettings/getPermissions';

export default function TodoChannel({ channel, socket }: { channel: Channel; socket: any; }) {
    const [focused, setFocused] = useState(false);
    const [task, setTask] = useState<null | string>(null);
    const { channelId } = useParams();
    const [taskError, setTaskError] = useState("");
    const [taskList, setTaskList] = useState<any[]>([]);
    const { teamId } = useParams();
    const queryClient = useQueryClient();
    const team = (queryClient.getQueryData(["team", teamId]) as any)?.team;
    const roles = (queryClient.getQueryData(["team", teamId, "roles"]) as any)?.roles as any;
    const members = queryClient.getQueryState(["team", teamId, "members"]) as any;
    const me = queryClient.getQueryState(["team", teamId, "members", "@me"]) as any;


    const tasks = useQuery({
        queryKey: ['tasks', channel.id],
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/v1/channels/${channel.id}/todo/list`);
            const data = await response.json();
            return data;
        }
    });

    useEffect(() => {
        if (tasks.isSuccess && tasks.data?.todos) setTaskList(tasks.data.todos);
    }, [tasks.isSuccess, tasks.data]);
    useEffect(() => {
        if (!socket) return;
        const handleMessage = (event: MessageEvent) => {
            const data = JSON.parse(event.data);
            if (data.t === "TODO_ITEM_CREATED" && data.d.todo.channelId === channel.id) {
                setTaskList((prevTasks) => [...prevTasks, data.d.todo]);
            } else if (data.t === "TODO_ITEM_DELETED") {
                setTaskList((prevTasks) => prevTasks.filter((task) => task.id !== data.d.todoId));
            } else if (data.t === "TODO_ITEM_UPDATED") {
                setTaskList((prevTasks) => {
                    const index = prevTasks.findIndex((task) => task.id === data.d.todo.id);
                    if (index === -1) return prevTasks;

                    prevTasks[index] = data.d.todo;
                    return [...prevTasks];
                });
            }
        };

        socket.addEventListener('message', handleMessage);

        return () => {
            socket.removeEventListener('message', handleMessage);
        };
    }, [socket, channel.id]);
    const createTask = async () => {
        let res = await fetch(`__SITE_BASE__/api/v1/channels/${channelId}/todo/item`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                content: task,
            })
        });

        let data = await res.json();
        if (!data.success) return setTaskError(data.message);
        setTask("");
    }
    const channelPermission = getPermissionsById({
        teamData: { data: { team } } as QueryObserverResult<{ team: Team }, Error>,
        userId: me?.data?.member?.id,
        channelData: channel,
        members: members?.data,
        roles: roles,
    });
    if (!channelPermission.allow.includes("VIEW_CHANNEL")) return

    return (
        <>
            <div className="flex flex-col w-full h-full border rounded-lg border-brand-dark">
                <TopChannel channel={channel} />

                <div className="flex p-2 flex-col-reverse overflow-auto overflow-x-hidden-900 "
                    style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(27 27 29)" }} >
                    <div className="flex flex-col gap-2 w-full">

                        {tasks.isLoading ? (

                            [...Array(1)].map((_, index) => (
                                <div key={index} className="flex flex-col gap-2 rounded-sm hover:bg-brand-darkest cursor-pointer">
                                    <div className="flex items-start gap-2 rounded-lg hover:bg-brand-darkest cursor-pointer">
                                        <div className="w-full h-16 animate-pulse rounded-sm bg-gray-600 font-semibold text-lg" />
                                    </div>
                                </div>
                            ))

                        ) : tasks.isError || tasks.data?.message ? (
                            <div className="flex justify-center items-center w-full h-32">
                                <ErrorBox message={(tasks.error || tasks.data?.message)?.toString()} />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2 py-2">
                                {taskList.map((task) => (
                                    <TodoChannelItem key={task.id} item={task} permissions={channelPermission} />
                                ))}
                            </div>
                        )}

                        <div className={`flex-col gap-2 items-center bg-brand-dark rounded-md ${focused ? "border-2 border-brand-green" : ""}`}>
                            {taskError && (
                                <div className="flex gap-2 px-2 sm:px-24 items-center p-2 cursor-pointer">
                                    <p className="text-red-500 font-semibold">{taskError}</p>
                                </div>
                            )}
                            {channelPermission.allow.includes("CREATE_TODOS") && (
                                <>
                                    <div className="flex px-2 sm:px-24 py-4">
                                        <div className="flex gap-2 text-brand-green items-center p-2 cursor-pointer">
                                            <Icon name="plus" size="20" />
                                        </div>
                                        <input maxLength={256} type="text" placeholder="Add a task" className="p-2 bg-brand-dark w-full rounded-lg text-white focus:outline-none" onFocus={() => {
                                            setFocused(
                                                channelPermission.allow.includes("CREATE_TODOS")
                                            )
                                        }} onBlur={() => setFocused(false)} onChange={(e) => setTask(e.target.value)} value={task!} />
                                    </div>
                                    {focused && (
                                        <div className="flex gap-2 px-2 sm:px-24 items-center p-2 cursor-pointer">
                                            <button className="bg-brand-green p-2 rounded-lg disabled:opacity-50 transition-all duration-300" disabled={!Boolean(task?.trim().length)} onMouseDown={createTask}>Add Task</button>
                                        </div>
                                    )}
                                </>
                            )}

                        </div>

                    </div>
                </div>

            </div>
        </>
    );
}
